import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Descriptions, Divider, Form, Input, message, Modal, Select, Tag, Typography} from 'antd';
import dayjs from 'dayjs';
import {useStaffData} from "./hooks/useStaffData";
import {useTotalsCalculator} from "./hooks/useTotalsCalculator";
import {StaffTable} from "./subsubcomponents/StaffTable";
import {StaffSelection} from "./subsubcomponents/StaffSelection";
import {salaryStatementsAPI} from "./CreateStatementModal";

const {Text} = Typography;
const {TextArea} = Input;
const {Option} = Select;

const EditStatementModal = ({visible, onCancel, onSubmit, refreshList, statementData}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [originalData, setOriginalData] = useState(null);
    const [staffDetails, setStaffDetails] = useState([]);
    const [status, setStatus] = useState('active');

    // Используем хук для работы с данными сотрудников
    const {
        staffLoading,
        availableStaff,
        selectedStaff,
        staffAmounts,
        handleAddStaff,
        handleRemoveStaff,
        handleAddAllStaff,
        handleAmountChange,
        resetStaffData,
        setSelectedStaff,
        setStaffAmounts
    } = useStaffData(visible, true, staffDetails);

    // Используем хук для расчета итогов
    const totals = useTotalsCalculator(staffAmounts);

    // Генерация списков годов и месяцев
    const yearOptions = useMemo(() => {
        const currentYear = dayjs().year();
        const years = [];
        for (let year = 2024; year <= currentYear + 1; year++) {
            years.push(year);
        }
        return years;
    }, []);

    const monthOptions = useMemo(() => {
        return Array.from({length: 12}, (_, i) => ({
            value: i + 1,
            label: dayjs().month(i).format('MMMM')
        }));
    }, []);

    // Инициализация данных при открытии модального окна
    useEffect(() => {
        if (visible && statementData) {
            console.log('Инициализация EditStatementModal с данными:', statementData);
            setOriginalData(statementData);
            setStaffDetails(statementData?.staff_details || []);
            setStatus(statementData?.status || 'active');

            // Устанавливаем данные формы
            const date = dayjs(statementData.date);
            form.setFieldsValue({
                name: statementData.name,
                description: statementData.description,
                year: date.year(),
                month: date.month() + 1,
            });
        }
    }, [visible, statementData, form]);

    // Обработчик клика по тегу статуса
    const handleStatusClick = useCallback(() => {
        const newStatus = status === 'active' ? 'inactive' : 'active';
        setStatus(newStatus);
        message.info(`Статус изменен на: ${newStatus === 'active' ? 'Активна' : 'Неактивна'}`);
    }, [status]);

    // Создаем редактируемую шапку
    const renderHeaderInfo = () => (
        <>
            <Divider style={{margin: '16px 0'}} titlePlacement="start">Основная информация</Divider>
            <Descriptions bordered column={2} style={{marginBottom: 24}} size={'small'}>
                <Descriptions.Item label="ID">
                    <Text strong>{statementData?.id}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Статус">
                    <Tag
                        color={status === 'active' ? 'green' : 'red'}
                        onClick={handleStatusClick}
                        style={{cursor: 'pointer', userSelect: 'none'}}
                        title="Кликните для изменения статуса"
                    >
                        {status === 'active' ? 'Активна' : 'Неактивна'}
                    </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Название">
                    <Form.Item
                        name="name"
                        style={{margin: 0}}
                        rules={[
                            {required: true, message: 'Введите название ведомости'},
                            {max: 100, message: 'Название не должно превышать 100 символов'}
                        ]}
                    >
                        <Input placeholder="Введите название ведомости"/>
                    </Form.Item>
                </Descriptions.Item>
                <Descriptions.Item label="Период">
                    <div style={{display: 'flex', gap: 8}}>
                        <Form.Item
                            name="year"
                            style={{margin: 0, flex: 1}}
                            rules={[{required: true, message: 'Выберите год'}]}
                        >
                            <Select placeholder="Год" style={{width: '100%'}}>
                                {yearOptions.map(year => (
                                    <Option key={year} value={year}>{year}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item
                            name="month"
                            style={{margin: 0, flex: 1}}
                            rules={[{required: true, message: 'Выберите месяц'}]}
                        >
                            <Select placeholder="Месяц" style={{width: '100%'}}>
                                {monthOptions.map(month => (
                                    <Option key={month.value} value={month.value}>
                                        {month.label}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </div>
                </Descriptions.Item>
                <Descriptions.Item label="Описание" span={2}>
                    <Form.Item
                        name="description"
                        style={{margin: 0}}
                        rules={[{max: 500, message: 'Описание не должно превышать 500 символов'}]}
                    >
                        <TextArea
                            placeholder="Введите описание (необязательно)"
                            autoSize={{minRows: 2, maxRows: 4}}
                        />
                    </Form.Item>
                </Descriptions.Item>
                <Descriptions.Item label="Дата создания">
                    <Text strong>{dayjs(statementData?.created_at).format('DD.MM.YYYY HH:mm')}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Дата обновления">
                    <Text strong>{dayjs(statementData?.updated_at).format('DD.MM.YYYY HH:mm')}</Text>
                </Descriptions.Item>
            </Descriptions>

            <Divider style={{margin: '16px 0'}} titlePlacement="start">Финансовые итоги</Divider>

            <Descriptions bordered column={4} style={{marginBottom: 24}} size={'small'}>
                <Descriptions.Item label="Количество сотрудников">
                    <Text strong>{selectedStaff.length}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Общая сумма">
                    <Text strong>{totals.totalAmount?.toFixed(2)} €</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Наличные">
                    <Text strong>{totals.totalCash?.toFixed(2)} €</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Безнал с НДС">
                    <Text strong>{totals.totalNonCashWithVAT?.toFixed(2)} €</Text>
                </Descriptions.Item>
            </Descriptions>
        </>
    );

    const handleSubmit = useCallback(async () => {
        try {
            const values = await form.validateFields();

            if (selectedStaff.length === 0) {
                message.error('Добавьте хотя бы одного сотрудника');
                return;
            }

            const formattedData = {
                ...values,
                date: `${values.year}-${values.month.toString().padStart(2, '0')}`,
                employeeCount: selectedStaff.length,
                totalAmount: totals.totalAmount,
                cashAmount: totals.totalCash,
                nonCashWithoutVAT: totals.totalNonCashWithoutVAT,
                nonCashWithVAT: totals.totalNonCashWithVAT,
                status: status, // Используем текущий статус из состояния
                staffIds: selectedStaff.map(staff => staff.id),
                staffDetails: selectedStaff.map(staff => ({
                    id: staff.id,
                    fullName: staff.fullName,
                    storeId: staff.storeId,
                    amounts: staffAmounts[staff.id] || {
                        cash: 0,
                        nonCashWithoutVAT: 0,
                        nonCashWithVAT: 0
                    }
                })),
                updatedAt: new Date().toISOString(),
            };

            setIsSubmitting(true);
            setLoading(true);

            try {
                const result = await salaryStatementsAPI.updateStatement(statementData.id, formattedData);

                if (result.success) {
                    message.success('Ведомость успешно обновлена');

                    if (onSubmit) {
                        onSubmit(formattedData);
                    }

                    form.resetFields();
                    resetStaffData();

                    if (refreshList) {
                        refreshList();
                    }

                    onCancel();
                } else {
                    message.error(result.message || 'Ошибка при обновлении ведомости');
                }
            } catch (error) {
                console.error('Ошибка при обновлении ведомости:', error);
                message.error('Ошибка при обновлении ведомости. Проверьте подключение к серверу.');
            }
        } catch (error) {
            console.error('Ошибка валидации:', error);
            if (error.errorFields) {
                message.error('Пожалуйста, заполните все обязательные поля правильно');
            }
        } finally {
            setLoading(false);
            setIsSubmitting(false);
        }
    }, [form, selectedStaff, staffAmounts, totals, onSubmit, refreshList, onCancel, resetStaffData, statementData, status]);

    const handleCancel = useCallback(() => {
        form.resetFields();
        resetStaffData();
        setOriginalData(null);
        setStaffDetails([]);
        onCancel();
    }, [form, onCancel, resetStaffData]);

    // Сброс при закрытии
    useEffect(() => {
        if (!visible) {
            resetStaffData();
            setOriginalData(null);
            setStaffDetails([]);
            // Восстанавливаем исходный статус при закрытии
            if (statementData) {
                setStatus(statementData.status);
            }
        }
    }, [visible, resetStaffData, statementData]);

    return (
        <Modal
            title={`Редактирование ведомости: ${statementData?.name || ''}`}
            open={visible}
            onOk={handleSubmit}
            onCancel={handleCancel}
            confirmLoading={loading}
            width={1200}
            okText="Сохранить"
            cancelText="Отмена"
            okButtonProps={{
                disabled: isSubmitting || selectedStaff.length === 0
            }}
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={{
                    year: dayjs().year(),
                    month: dayjs().month() + 1,
                }}
                validateTrigger="onBlur"
            >
                {/* Редактируемая шапка с полями внутри Descriptions */}
                {renderHeaderInfo()}

                <Divider style={{margin: '16px 0'}} titlePlacement="start">Сотрудники в ведомости
                    ({selectedStaff.length})</Divider>

                <StaffTable
                    selectedStaff={selectedStaff}
                    staffAmounts={staffAmounts}
                    handleAmountChange={handleAmountChange}
                    handleRemoveStaff={handleRemoveStaff}
                    totals={totals}
                />

                {availableStaff.length > 0 && (
                    <>
                        <Divider style={{margin: '16px 0'}} titlePlacement="start">Добавить сотрудников</Divider>
                        <StaffSelection
                            staffLoading={staffLoading}
                            availableStaff={availableStaff}
                            handleAddStaff={handleAddStaff}
                            handleAddAllStaff={handleAddAllStaff}
                            disabled={staffLoading}
                        />
                    </>
                )}

                {availableStaff.length === 0 && selectedStaff.length > 0 && (
                    <div style={{marginTop: 16, textAlign: 'center'}}>
                        <p>Все доступные сотрудники уже добавлены в ведомость</p>
                    </div>
                )}
            </Form>
        </Modal>
    );
};

export default EditStatementModal;